import { isEqual } from 'lodash';
import qs from 'qs';

import { canUseDOM } from './dom';

function decoder(str, defaultDecoder, charset, type) {
  // this decoder should get us the behavior we typically expect from deserialized values
  // if it's a number, coerce to a number, "true" becomes the boolean literal `true`, etc.

  // safe guard for a specific edge case with some encodings
  if (charset === 'iso-8859-1') {
    const strWithoutPlus = str.replace(/\+/g, ' ');
    return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
  }

  if (type === 'value' && /^[+-]?\d+(\.\d+)?$/.test(str)) {
    return parseFloat(str);
  }

  const keywords = {
    true: true,
    false: false,
    null: null,
    // eslint-disable-next-line object-shorthand
    undefined: undefined, // explicit undefined
  };
  if (type === 'value' && str in keywords) {
    return keywords[str];
  }

  return defaultDecoder(str, defaultDecoder, charset);
}

function nestedArrayFilter(prefix, value) {
  // in the case of serializing nested arrays, qs default behavior has an ambiguity problem
  // e.g. consider ?arr[][]=1&arr[][]=2 - is this { arr: [[1], [2]] } or { arr: [[1, 2]] }
  // so, with this filter we're going to preserve the default behavior in most cases (anything but nested arrays)
  // but, with nested arrays, we'll effectively switch to `arrayFormat: 'indices'` behavior temporarily
  if (Array.isArray(value) && value.some(child => Array.isArray(child))) {
    return value.reduce((acc, currentItem, index) => {
      acc[index] = currentItem;
      return acc;
    }, {});
  }

  // let qs handle it normally
  return value;
}

function hasSequentialKeys(obj) {
  if (typeof obj !== 'object') {
    return false;
  }
  const keys = Object.keys(obj);
  if (keys.length === 0) {
    return false;
  }
  return keys.every((key, index) => Number(key) === index);
}

function convertSequentialObjectsToArrays(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // outer object is an array already, recurse into each item
  if (Array.isArray(obj)) {
    return obj.map(convertSequentialObjectsToArrays);
  }

  const result = {};
  Object.keys(obj).forEach(key => {
    // recurse into object
    result[key] = convertSequentialObjectsToArrays(obj[key]);
  });

  // Check if this object should be an array
  if (hasSequentialKeys(result)) {
    // Converts { "0": "a", "1": "b" } into ["a", "b"]
    return Object.values(result);
  }

  return result;
}

const stringifyOptions = {
  encodeValuesOnly: true,
  arrayFormat: 'brackets',
  skipNulls: true,
  filter: nestedArrayFilter,
};
export const stringify = obj => qs.stringify(obj, stringifyOptions);

const parseOptions = {
  ignoreQueryPrefix: true,
  decoder,
};

function overrideDecodedTypes(query) {
  // Since the filters are now scoped, we need to dig into the scopes to find the applied filters
  return Object.entries(query).reduce((acc, [scope, scopedParams]) => {
    if (typeof scopedParams === 'object' && scopedParams !== null) {
      const updatedScopedParams = { ...scopedParams };

      // Convert the keys `q`, `search`, or `path` to strings if they exist
      ['q', 'search', 'path'].forEach(key => {
        if (key in updatedScopedParams) {
          updatedScopedParams[key] = updatedScopedParams[key]?.toString();
        }
      });
      acc[scope] = updatedScopedParams;
    } else {
      acc[scope] = scopedParams;
    }

    return acc;
  }, {});
}

export const parse = str => convertSequentialObjectsToArrays(overrideDecodedTypes(qs.parse(str, parseOptions)));

const mergePathParam = values => {
  if (!canUseDOM()) {
    return values;
  }
  if (typeof PB_WILDCARD_BASE !== 'undefined' && typeof PB_WILDCARD !== 'undefined') {
    const { pathname } = document.location;
    let wildcard = pathname.match(new RegExp(PB_WILDCARD_BASE.replace('/*', '/(?<param>.*)')))?.groups?.param;
    if (wildcard && wildcard.indexOf(',') !== -1) {
      wildcard = wildcard.split(',');
    }
    values = {
      ...values,
      ...(wildcard ? { [PB_WILDCARD]: wildcard } : {}),
    };
  }
  return values;
};

const getParams = () => {
  const queryStr = canUseDOM() ? window?.location?.search : '';

  return mergePathParam(parse(queryStr));
};

const getPathAndQuery = params => {
  if (typeof PB_WILDCARD_BASE !== 'undefined' && typeof PB_WILDCARD !== 'undefined') {
    let { [PB_WILDCARD]: wildcard, ...rest } = params;
    if (Array.isArray(wildcard)) {
      wildcard = wildcard.join(',');
    }
    return {
      pathname: `/${PB_WILDCARD_BASE.replace('/*', wildcard ? `/${wildcard}` : '')}`,
      queryParams: rest,
    };
  }
  return {
    pathname: document.location.pathname,
    queryParams: params,
  };
};

const setParams = params => {
  if (!canUseDOM()) {
    return;
  }
  const { pathname, queryParams } = getPathAndQuery(params);
  const { hash } = window.location;
  const paramStr = stringify(queryParams);
  // don't add trailing '?' if params are blank
  if (paramStr.length > 0) {
    window.history.replaceState(null, '', `${pathname}?${paramStr}${hash}`);
  } else {
    window.history.replaceState(null, '', `${pathname}${hash}`);
  }
};

// install some hooks URL update events. popstate doesn't seem reliable.
const installHistoryHooks = () => {
  let currentParams;
  let previousParams;
  // emit paramsChanged if params are different
  const emitIfParamsChanged = () => {
    previousParams = currentParams;
    currentParams = getParams();
    if (!isEqual(currentParams, previousParams)) {
      window.dispatchEvent(new Event('paramsChanged'));
    }
  };

  const { pushState, replaceState } = window.history;
  window.history.pushState = function (...args) {
    pushState.apply(window.history, args);
    emitIfParamsChanged();
  };

  window.history.replaceState = function (...args) {
    replaceState.apply(window.history, args);
    emitIfParamsChanged();
  };
};
// trying to not blow up SSR. not sure if necessary or not.
if (canUseDOM()) {
  installHistoryHooks();
}

// only fired when URL params are changed (by hash comparison)
const subscribeToParamUpdate = callback => {
  canUseDOM() && window?.addEventListener && window.addEventListener('paramsChanged', callback);
};

export { getParams, setParams, subscribeToParamUpdate };
